import type { SagaCond } from './saga.ts';
import type { SendaDef, SendaLine } from './sendas.ts';
import { sec } from './units.ts';

/**
 * The cleric's path, El último camino (docs/sendas/clerigo.md): its numbers and its chapters, in a file of its own so
 * the paths' branches meet without stepping on each other (content/sendas.ts lists it with the others).
 *
 * Chapter 1, «La camilla» (level 18): a stretcher with a lantern on a pole goes down Musgoviejo's lower street at night
 * (the cinematic); Odna presents Tessa, an old cleric of the Order who wants to be buried in the Hermitage up the
 * Spine; the lower street (the stretcher out of the village, the stalkers only seen in its lantern's light); the chapel
 * at the crossroads (the first time of the mirrors: the lantern hung on one of two hooks, two mirrors, the shepherds'
 * door opens with light); the campfire with Tessa in the chapel; and the test, the slope of the limpets (curing her is
 * not enough if they stay stuck: a holy blow peels them, and one peeled jumps on whoever struck it); the close at the
 * top of the slope, the boy from Musgoviejo and the news that Joran is coming up.
 */

/**
 * The stretcher and its fights (battle/litter.ts, battle/litter-layout.ts):
 *
 * - `near`: the stretcher goes on at `pace` a tick while the hero keeps within it and both carriers stand.
 * - a carrier down sets the stretcher on the ground: the hero beside them (`liftReach`) for `lift` gets them up, with
 *   `liftBp` of their blows.
 * - `mendReach`: a heal of the cleric's on a hero this near Tessa heals her the same share of her life (`mendBp` of it).
 * - `light`: the lantern's light; stalkers are seen only inside it.
 * - `blow`: each nightmare at the stretcher lands a blow on the nearest of its three every `every`, within `range`; one
 *   this near the hero (`hold`) fights the hero instead.
 */
export const CAMINO = {
  litter: {
    near: 4000,
    pace: 13,
    lift: sec(2),
    liftReach: 1400,
    liftBp: 5000,
    mendReach: 2600,
    mendBp: 5000,
    light: 2900,
    blow: { every: sec(1), range: 950 },
    hold: 1500,
  },
  /**
   * The lower street (act 1): stalkers out of the alleys `ahead` rows or so ahead of the stretcher (or behind it), `n`
   * at a time, the first at `first` and then every `gap`; soot crows off the roofs at the hero, `n` every `every`.
   * `hpBp`, `dmgBp`: the act's nightmares over the chapter's commons.
   */
  street: {
    stalker: { first: sec(6), gap: [sec(6), sec(8)] as [number, number], n: [1, 2] as [number, number], ahead: 6 },
    crows: { first: sec(24), every: sec(30), n: 4 },
    hpBp: 10000,
    dmgBp: 8500,
  },
  /**
   * The chapel at the crossroads (act 2; §4 «La capilla del cruce»): a hook or a mirror worked standing `stand` within
   * `reach` of it; the beam burns the ink it crosses `beamD` seconds of D a second; the door opens `open` after the lamp
   * lights. Stalkers come for the stretcher one every `every` from `first` (in the dark while the lantern hangs).
   */
  chapel: { stand: sec(1), reach: 1350, beamD: 1, open: sec(2.5), stalker: { first: sec(8), every: sec(7.75) }, hpBp: 10000, dmgBp: 10000 },
  /**
   * The slope of the limpets (act 3, the test): `d` seconds of D each; they come out of the ground at each bend as the
   * stretcher nears it (`near`: `burst` of them) and one every `trickle` between, `cap` on the field at most; they crawl at
   * `speed` and, stuck to the stretcher, take `drainBp` of Tessa's life a second each. A holy blow peels one: it jumps
   * on the hero who struck it (`leap`) and takes `heroBp` of the hero's life a second until it dies. Soot crows peck the
   * carriers, `n` every `every`.
   */
  slope: {
    limpet: { d: 2, speed: 650, drainBp: 300, heroBp: 800, burst: 4, near: 3200, trickle: sec(10), cap: 8, leap: sec(0.5) },
    crows: { first: sec(30), every: sec(35), n: 2 },
    hpBp: 10000,
    dmgBp: 10000,
  },
  /**
   * Chapter 2, the cliff path (act 1; battle/pasarela.ts): the stretcher carried at `pace` from chapel to chapel, waiting
   * in each until the hero says go; within `shade` of a chapel (or in a sanctuary) the stones don't reach. The slingers
   * across the fog throw a volley every `every` from `first`, its marks on the ground `warn` before: each of the three
   * out of shelter takes `blows` (Tessa `tessa`), the hero in a mark (`r` across) `heroBp` of its life. Gargoyles come
   * off the rock at the carriers, `n` every `every`.
   */
  ridge: {
    pace: 40,
    shade: 1500,
    volley: { first: sec(7), every: sec(11), warn: sec(3), r: 800, heroBp: 1400, blows: 2, tessa: 3 },
    gargoyle: { first: sec(10), every: sec(12), n: [1, 1] as [number, number] },
    hpBp: 8000,
    dmgBp: 9500,
  },
  /**
   * Chapter 2, the walkway (act 2, the puzzle «La luna en la pasarela»): four spans over the fog, a mirror on the post at
   * the start of each. A span the moon lies on is clean of ink after `burn`; one left dark is covered again `cover`
   * after. The stretcher goes at `pace` on clean planks only, while the hero keeps within `near`; a mirror is turned by
   * standing `stand` within `reach` of its post. The hero on ink or on a torn plank falls to the nets and climbs back at
   * the span's post (`fallBp` of its life). Piet slips on span `slip.span` (from 0) and hangs: the hero beside him for
   * the stretcher's `lift` gets him up before the gargoyle lets him go (`slip.bar`). Gargoyles land on a plank ahead and
   * tear it after `tear` (killed first, it holds); the hero beside a torn plank for `mend` lays it again. The moon burns
   * what stands in it `beamD` seconds of D a second.
   */
  walk: {
    pace: 60,
    near: 5600,
    burn: sec(2),
    cover: sec(8),
    stand: sec(1),
    reach: 1350,
    fallBp: 1000,
    slip: { span: 2, bar: sec(12) },
    gargoyle: { first: sec(10), every: sec(7), tear: sec(6) },
    mend: sec(2),
    mendReach: 1400,
    beamD: 1,
    hpBp: 6500,
    dmgBp: 10000,
  },
  /**
   * Chapter 2, the chapels' bridge (act 3, the test): planks hung from chains between two chapels. The stretcher goes on
   * at `pace` when the hero says go and keeps near, never onto a torn plank; over one for `hang` it falls. The hero beside
   * a torn plank for `mend` lays it again. The bone mangonel on the far chapel (`mangonel.d` seconds of D; never stunned
   * nor taunted) throws a stone every `every` from `first` at the stretcher's way, its shadow `warn` before: where it will
   * be when it lands if it goes on, the plank in front of it if it waits. It breaks that plank and the next one on,
   * `tessa` blows on her if the stretcher is in it (`r`), `heroBp` of the hero's life if the hero is.
   * Gargoyles land on a plank by the stretcher and tear it after `tear`. The limpets climb out of the fog to it (as on
   * the slope).
   */
  bridge: {
    pace: 30,
    hang: sec(3),
    mend: sec(2),
    mendReach: 1400,
    mangonel: { d: 85, first: sec(9), every: sec(8), warn: sec(3), r: 900, heroBp: 3500, tessa: 2 },
    gargoyle: { first: sec(12), every: sec(11), tear: sec(6) },
    limpet: { d: 2, speed: 650, drainBp: 300, heroBp: 1000, trickle: sec(7), cap: 6, leap: sec(0.5) },
    hpBp: 7000,
    dmgBp: 10000,
  },
  /**
   * Chapter 3, Tessa's cold (battle/nieve.ts): a bar from `full` down. It drops `drop` a tick while the stretcher goes
   * on and `still` while it stands; a lit brazier within `warmR` of her brings it up `warm` a tick instead. At the bottom
   * she takes a blow every `bite`. A brazier is lit by the hero standing `light` within `reach` of it.
   */
  cold: { full: 10000, drop: 21, still: 40, warm: 80, warmR: 2300, bite: sec(0.75), light: sec(3), reach: 1350 },
  /**
   * Chapter 3, the white col (act 1): the stretcher carried at `pace` up the col in the blizzard, braziers in the
   * chapels by the way; wolves out of the snow, `n` every `every` from `first`, at the stretcher (`hpBp`, `dmgBp` over
   * the chapter's commons). Joran and his apprentices `behind` points of the way back, with their own fire.
   */
  col: {
    pace: 15,
    wolf: { first: sec(8), every: sec(9), n: [2, 3] as [number, number] },
    behind: 6,
    hpBp: 9000,
    dmgBp: 10000,
  },
  /**
   * Chapter 3, the frozen mirrors (act 2, the puzzle; §4 «Los espejos helados»): three braziers carried at `carryBp` of
   * the hero's pace, set on a base and lit (`cold.light`); taken up again in `take`. A lit brazier thaws the mirrors by
   * its base after `thaw`; with no lit brazier by it any more a mirror frosts over again after `refreeze`. A thawed
   * mirror is turned by standing `stand` by it. The door opens `open` after the light reaches it. Wolves at the
   * stretcher, one every `wolf.every` from `wolf.first`, and one more each `wolf.grow` the terrace holds out. Tessa's cold here drops `drop` a tick with no brazier by her.
   */
  frost: {
    carryBp: 7000,
    take: sec(1),
    thaw: sec(1.5),
    refreeze: sec(4),
    stand: sec(1),
    reach: 1350,
    open: sec(2.5),
    drop: 14,
    beamD: 1,
    wolf: { first: sec(12), every: sec(9), grow: sec(55) },
    hpBp: 9000,
    dmgBp: 9500,
  },
  /**
   * Chapter 3, the night in the corral (act 3, the test): `night` long. Moths of sleep come for the lit braziers, `n`
   * every `every` from `first`; one at a brazier `snuff` puts it out and burns in it. Wolves through the gaps, `n` every `every`. The
   * ash watcher (era V; `watcher.d` seconds of D) dives every `every` from `first` on the weakest in the corral, the
   * shadow of its wings on the floor `warn` before: it lands where the shadow is (`r`), strikes whoever stands in it
   * (`blows` on one of the stretcher's or the family, `heroBp` of the hero's life) and puts out the lit brazier nearest
   * to whom it struck; it stays down `down` (`downHeld` if the hero took it), only then can it be struck. One of the
   * family struck with no lit brazier within `freezeR` is left frozen until the hero gets them up (the stretcher's
   * `lift`). Tessa's cold drops `drop` a tick with no lit brazier by her.
   */
  corral: {
    night: sec(420),
    moth: { first: sec(14), every: sec(16), n: 3, snuff: sec(2) },
    wolf: { first: sec(20), every: sec(22), n: [1, 1] as [number, number] },
    watcher: { d: 110, first: sec(16), every: sec(13), warn: sec(3), r: 1000, heroBp: 3000, blows: 2, down: sec(2.5), downHeld: sec(4.5) },
    freezeR: 2000,
    drop: 9,
    family: 3,
    hpBp: 8500,
    dmgBp: 10000,
  },
  /**
   * Chapter 4 (battle/ermita.ts): a mirror or a divider in the nave turned by standing `stand` within `reach` of it.
   */
  hermit: { stand: sec(1), reach: 1350 },
  /**
   * Chapter 4, the atrium (act 1): a descent of three halls cut in the rock, the dead pilgrims, a bone singer and the
   * mourners that mend the rest (a crypt's, by `bestiary` in the chapter).
   */
  atrium: { hpBp: 10000, dmgBp: 10000 },
  /**
   * Chapter 4, the memory of Aud's last vigil (act 2): the dawn at `dawn`; the six lamps from the door to the altar, each
   * one able to be lit from `every` × its place on, by standing `light` by it. The Doliente comes in at `in` and glides
   * `glide` a tick; while a lamp ahead of her is lit she stops there and sends a tongue of ink at it every
   * `tongue.every` (crawling `tongue.speed` a tick, `tongue.d` seconds of D of life): held at the lamp `tongue.snuff` it puts it
   * out (`tongue.fast` after a wail not cut), and she comes on to it. A lamp lit again behind her drives her back to it. Her
   * wail: from `wail.first`, every `wail.every`, warned `wail.warn` before, `wail.long` long, no heal works in it; `wail.hush`
   * seconds of D of holy damage on her in it cut it and she keeps quiet `wail.mute`. Her veil on whoever is within
   * `veil.r`: `veil.bp` of its life every `veil.every`, through armour. The dead in from the atrium, `n` every `every`;
   * a bone singer at `singer`. Lost with the altar's lamp put out (or not lit at dawn).
   */
  vigil: {
    dawn: sec(360),
    every: sec(60),
    light: sec(2),
    in: sec(6),
    glide: 40,
    tongue: { every: sec(8), speed: 42, d: 4, snuff: sec(3), fast: sec(2) },
    wail: { first: sec(20), every: sec(20), warn: sec(3), long: sec(6), hush: 2, mute: sec(6) },
    veil: { r: 1600, bp: 1000, every: sec(4) },
    dead: { first: sec(14), every: sec(30), n: [1, 2] as [number, number] },
    singer: sec(120),
    hpBp: 7000,
    dmgBp: 10000,
  },
  /**
   * Chapter 4, the nave of the mirrors (act 3, the test; §4): Aud's mirror taken from the loose stone and set in its place
   * (`take` each); the beam lights the altar and the door: both lit `hold` and it is won. The beam burns the ink it
   * crosses `beamD` seconds of D a second. Limpets out of the ink on the walls (as on the slope), the first at `first`,
   * then one every `trickle`, `cap` at most: `mirrorBp` of them go for a mirror on the beam's way (a mirror under a limpet
   * stops it), the rest for Tessa on the altar. The great bone singer (`singer` seconds of D) keeps by the door and sings
   * its dirge from `dirge.first`, every `dirge.every`, its bones lit `dirge.warn` before: a rain of bones on the hero
   * wherever in the nave, `dirge.bp` of its life through armour, until it is down. The dead from the cells, one more
   * each `dead.grow` the nave holds out (the night doesn't wait).
   */
  nave: {
    take: sec(1),
    hold: sec(3),
    beamD: 1,
    limpet: { d: 2, speed: 650, drainBp: 300, heroBp: 800, trickle: sec(8), cap: 6, first: sec(14), mirrorBp: 5200, leap: sec(0.5) },
    singer: 130,
    dirge: { first: sec(16), every: sec(12), warn: sec(2), bp: 4000 },
    dead: { first: sec(20), every: sec(28), n: [1, 1] as [number, number], grow: sec(60) },
    hpBp: 9000,
    dmgBp: 10000,
  },
  /**
   * Chapter 5, the lamps (act 1; battle/vigilia.ts): the nave's six lamps of the vigil, one free each `every`, lit by
   * standing `light` by it; a lamp lit (or lit again) peels with its ring of light every limpet within `ring` (`ringD`
   * seconds of D of holy light). Limpets out of the ink go for a lit lamp (`limpet`: one every `every` from `first`,
   * `cap` at most; stuck on it `snuff`, it goes out); the dead in from the cells go for a lit lamp too and put it out with
   * `hits` blows (one every `blow`); gargoyles down the hole in the roof at the hero (`garg`); a bone singer by the atrium
   * at `singer`. Lost with two lamps out at once; won with all six lit, `settle` after.
   */
  lamps: {
    every: sec(60),
    light: sec(2),
    ring: 4000,
    ringD: 3,
    hits: 3,
    blow: sec(1.5),
    limpet: { d: 2, speed: 650, first: sec(12), every: sec(10), cap: 4, snuff: sec(4) },
    dead: { first: sec(18), every: sec(22), n: [1, 2] as [number, number] },
    garg: { first: sec(36), every: sec(48), n: 2 },
    singer: sec(170),
    settle: sec(2),
    hpBp: 9000,
    dmgBp: 17000,
  },
  /**
   * Chapter 5, the atrium (act 2; battle/vigilia.ts): the Doliente's procession in by the atrium's outer door, a band
   * every `band.every` from `band.first` to `band.until` (`band.n` dead each; a bone singer leads every `singerEvery`-th,
   * a mourner every `mournerEvery`-th), for the nave's door: Piet bars it from behind, `door` blows of the procession's
   * (one each `blow` from whoever stands at it) and it breaks. Joran and his apprentices fight in the middle (`guard`:
   * their blow `hitD` seconds of D every `every`); down, they stay down until the hero stands by them `lift`. Won with
   * the procession all in and down, `settle` after.
   */
  procession: {
    door: 60,
    blow: sec(2),
    bite: sec(3),
    band: { first: sec(6), every: sec(18), until: sec(250), n: [2, 3] as [number, number] },
    singerEvery: 3,
    mournerEvery: 2,
    guard: { every: sec(1.5), hitD: 0.7, reach: 1000, guard: 2600, speed: 1100 },
    lift: sec(2),
    liftBp: 5000,
    settle: sec(2),
    hpBp: 8500,
    dmgBp: 18000,
  },
  /**
   * Chapter 5, the mirrors again (act 3, the test; §4 «Los espejos otra vez»): chapter 4's nave (the one won), Aud's mirror
   * in its place, `moved` of the way's pieces turned by the wind (the seed says which); more limpets than in chapter 4,
   * the dead from the cells; soot crows down by the hole in the roof at the hero (`crows`), and the wind's hail down it on
   * the hero wherever (`hail`: warned `warn`, `bp` of its life through armour); no singer. Won with the altar and the door
   * lit at once `hold`.
   */
  renave: {
    moved: 3,
    hold: sec(3),
    crows: { first: sec(4), every: sec(11), n: 6 },
    hail: { first: sec(8), every: sec(11), warn: sec(2), bp: 3400 },
    limpet: { trickle: sec(8), cap: 7, first: sec(6), mirrorBp: 6500 },
    dead: { first: sec(14), every: sec(22), n: [1, 2] as [number, number], grow: sec(60) },
    hpBp: 9000,
    dmgBp: 10000,
  },
  /**
   * Chapter 5, the Doliente (act 4; §5; battle/vigilia.ts). Her life `d` seconds of D; she glides `glide` a tick and keeps
   * `keep` off the candle (by the altar) until `hunt`, then puts herself between the hero and the altar.
   *
   * - Her veil on whoever stands within `veil.r` of her (it trails metres: it reaches anyone in bow's reach of her):
   *   `veil.bp` of its life every `veil.every`, through armour. Her embrace (`hug`): the veil opens in wings `warn` before,
   *   then `bp` of the life of whoever is within `r` of her.
   * - Her wail (`wail`): every `every` (`every2` from `split`; `sing` later in the truth, Tessa singing), warned `warn`,
   *   `long` long, no heal works; `hush` seconds of D of holy damage on her in it cut it, and she is quiet `mute` (taking
   *   `mutedBp` of what lands). Each wail raises `limpets` limpets (one less each quarter of her life gone): for Tessa and
   *   the door's people (the believe: not Tessa); from `split` one of them for a mirror of the way. Not cut, the candle
   *   shrinks: the next tongue puts it out at `tongue.fast`. From `hunt` she wails without pause while not in the sun.
   * - Her tongues (`tongue`): one at a time, `gap` after the last, crawling `speed` a tick down a side of the nave to the
   *   candle; from `split`, two at once, one down each side. `d` seconds of D of life; held at the candle `snuff` it goes
   *   out (`believe` in the believe; `fast` after an uncut wail; `nervous` less with Piet or Ludo down). The beam burns
   *   what crosses it (`lanternD` a second; the sun's `sunD`), and Joran holds one at the candle `hold.ticks` once every
   *   `hold.every`. Each scar of hers (a try lost past her half or past `sun`) slows them `scarBp`.
   * - `split`: the veil splits, Ludo and the muleteers come in by the atrium (a muleteer cleans with vinegar a covered
   *   mirror of the way each `vinegar`), and the dead in from the atrium (`dead`). `frost`: a mirror of the way frosts
   *   over (a brazier by it thaws it, standing `thaw` by it). `sun`: the first light; on the way set right it stays on
   *   the altar and the door, `sunD` a second on her while in it. `dawn`: the sun is up, and the candle lit wins.
   * - The people: Joran by the candle, Piet and the apprentices at the door; a limpet on one of them lands a blow every
   *   `bite`; down, they stay down until the hero stands by them `lift`.
   */
  mourn: {
    d: 400,
    glide: 30,
    keep: 5200,
    veil: { r: 4300, bp: 1200, every: sec(4) },
    hug: { first: sec(44), every: sec(28), warn: sec(3), bp: 3500, r: 2400 },
    wail: { first: sec(14), every: sec(16), every2: sec(12), warn: sec(3), long: sec(6), hush: 3, mute: sec(6), mutedBp: 13000, sing: sec(2) },
    limpets: 3,
    tongue: { d: 4, speed: 42, gap: sec(7), snuff: sec(3), believe: sec(6), fast: sec(2), nervous: sec(1), lanternD: 1, sunD: 20, hold: { r: 1000, ticks: sec(2), every: sec(20) }, scarBp: 1000 },
    sunD: 1,
    split: sec(180),
    vinegar: sec(20),
    frost: { at: sec(260), thaw: sec(3) },
    sun: sec(360),
    hunt: sec(360),
    dawn: sec(540),
    dead: { first: sec(195), every: sec(32), n: [1, 2] as [number, number] },
    bite: sec(3),
    lift: sec(2),
    liftBp: 5000,
    guard: { every: sec(1.5), hitD: 0.6, reach: 1000, guard: 2400, speed: 1100 },
    hpBp: 9000,
    dmgBp: 15000,
  },
};

const ln = (id: string, who: string, cond?: SagaCond): SendaLine => (cond ? { id, who, if: cond } : { id, who });
/** The chapter 4 decision, as its close and chapter 5 read it: Tessa told the Hermitage is broken, or let believe it. */
const TRUTH: SagaCond = { flag: 'truth' };
const BELIEVE: SagaCond = { flag: 'believe' };

/** The people of Musgoviejo carrying the stretcher (the same three in every fight of the chapter). */
const BEARERS = [
  { id: 'piet', does: 'wait' as const, hits: 6, vital: false },
  { id: 'ludo', does: 'wait' as const, hits: 6, vital: false },
  { id: 'tessa', does: 'wait' as const, hits: 20, model: 'tessaLitter' },
];

export const CAMINO_PATH: SendaDef = {
  id: 'cleric',
  cls: 'cleric',
  planned: 5,
  cast: {
    odna: { npc: 'odna' },
    tessa: { model: 'tessaLitter', portrait: 'tessa' },
    piet: { model: 'piet' },
    ludo: { model: 'ludo' },
    // Madre Aud speaks by the inscriptions cut by each mirror.
    aud: { voice: true },
    boy: { model: 'musgoBoy' },
    // Chapter 2: a muleteer of the cliff road at the second chapel, his lame mule beside him; Joran and his two apprentices.
    arriero: { model: 'arriero' },
    joran: { model: 'joran' },
    apprenticeA: { model: 'apprentice' },
    apprenticeB: { model: 'apprenticeB' },
    // Chapter 3: Piet's mother and his two little brothers, in the corral.
    pietMother: { model: 'pietMother' },
    kidA: { model: 'pietKid' },
    kidB: { model: 'pietKidB' },
    // Chapter 4: Madre Aud as she is played in the memory, Tessa at twenty-two, and her mother on the altar.
    audPast: { model: 'aud' },
    tessaYoung: { model: 'tessaYoung' },
    tessaMother: { model: 'tessaMother' },
    // Chapter 5: a second muleteer of the cliff road who comes up with Ludo (his mule behind him, a skin of vinegar on it).
    arrieroB: { model: 'arriero' },
  },
  chapters: [
    {
      id: 'litter',
      closedBy: 'tessa',
      piece: 'shepherdBand',
      acts: [
        {
          // Musgoviejo at night, its windows dark: a stretcher comes out down the lower street, a lantern hung from a
          // pole, its light moving over the walls.
          kind: 'scene',
          id: 'cine',
          lines: [],
          cine: {
            video: 'senda-cleric-1',
            place: 'musgoNight',
            sky: 'night',
            weather: 'snow',
            cam: [
              // (Axial: the lane runs up the board's middle, x = 0, so q = −r/2.)
              { from: [1.0, -2.0, 4.6], at: [-2.2, 4.6, 0.5], ms: 5200 },
              { from: [0.4, -0.8, 2.4], at: [-1.8, 3.4, 0.6], ms: 5200 },
              { from: [-0.3, 0.6, 1.4], at: [-1.6, 2.6, 0.6], ms: 5000 },
            ],
            figures: [
              { who: 'piet', q: -2, r: 4, facing: 4.5, pose: 'walk', delay: 400, speed: 12, walkMs: 14000 },
              { who: 'tessa', q: -2, r: 5, facing: 4.5, pose: 'walk', delay: 400, speed: 12, walkMs: 14000 },
              { who: 'ludo', q: -3, r: 6, facing: 4.5, pose: 'walk', delay: 400, speed: 12, walkMs: 14000 },
            ],
          },
        },
        {
          kind: 'scene',
          id: 'open',
          lines: [ln('l1', 'odna'), ln('l2', 'odna'), ln('l3', 'tessa'), ln('l4', 'tessa'), ln('l5', 'tessa'), ln('l6', 'ludo')],
          cine: {
            place: 'musgoHall',
            sky: 'night',
            cam: [
              { from: [0, 4.2, 3.6], at: [0, -0.8, 0.6], ms: 8000 },
              { from: [-1.4, 2.6, 1.8], at: [0.2, 0, 0.5], ms: 9000 },
              { from: [1.6, 2.0, 0.9], at: [-0.3, 0.4, 0.35], ms: 9000 },
            ],
            figures: [
              { who: 'odna', q: -1, r: -1, facing: 1.2, pose: 'stand' },
              { who: 'tessa', q: 0, r: 0, facing: 0, pose: 'stand' },
              { who: 'piet', q: 1, r: -1, facing: 2.4, pose: 'stand' },
              { who: 'ludo', q: -1, r: 1, facing: 5.4, pose: 'stand' },
              { who: 'hero', q: 1, r: 1, facing: 3.8, pose: 'stand' },
            ],
          },
        },
        {
          // The lower street at night, between shut houses: the stretcher out of the village (§3, act 1).
          kind: 'fight',
          id: 'street',
          mode: 'street',
          place: 'musgoStreet',
          weather: 'snow',
          night: true,
          budgetBp: 9000,
          goal: { kind: 'litter' },
          npcs: BEARERS,
          says: [
            { on: 'start', who: 'piet', line: 'l1', delay: sec(3) },
            { on: 'start', who: 'tessa', line: 'l2', delay: sec(6) },
            { on: 'cue', cue: 'stalker', who: 'tessa', line: 'l3' },
            { on: 'cue', cue: 'down1', who: 'tessa', line: 'l4' },
            { on: 'cue', cue: 'gate', who: 'ludo', line: 'l5' },
          ],
        },
        {
          // The shepherds' terraces out of the village; a small chapel at the crossroads, its door shut: it opens with
          // light (§3, act 2; §4, the mirrors small).
          kind: 'fight',
          id: 'chapel',
          mode: 'mirrors',
          place: 'crossChapel',
          weather: 'snow',
          night: true,
          budgetBp: 9000,
          goal: { kind: 'lamp' },
          npcs: BEARERS,
          says: [
            { on: 'start', who: 'tessa', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'inscription', who: 'aud', line: 'l2' },
            { on: 'cue', cue: 'turn1', who: 'tessa', line: 'l3', delay: sec(1) },
            { on: 'cue', cue: 'dark', who: 'piet', line: 'l4' },
            { on: 'cue', cue: 'lit', who: 'piet', line: 'l5' },
          ],
        },
        {
          kind: 'fire',
          id: 'fire',
          with: ['tessa'],
          questions: [
            { id: 'order', lines: [ln('l1', 'tessa'), ln('l2', 'tessa')], set: ['askedOrder'] },
            { id: 'hermitage', lines: [ln('l1', 'tessa'), ln('l2', 'tessa')], set: ['askedHermitage'] },
            { id: 'fear', lines: [ln('l1', 'tessa'), ln('l2', 'tessa')], set: ['askedFear'] },
          ],
          cine: {
            place: 'chapelFire',
            sky: 'night',
            cam: [
              { from: [2.6, 3.0, 2.1], at: [-0.3, -0.3, 0.35], ms: 9000 },
              { from: [-1.6, 3.2, 1.6], at: [0.1, -0.3, 0.4], ms: 9000 },
            ],
            figures: [
              { who: 'tessa', q: -1, r: 1, facing: 5.4, pose: 'stand' },
              { who: 'piet', q: 0, r: 1, facing: 4.6, pose: 'sit' },
              { who: 'ludo', q: -2, r: 0, facing: 0.3, pose: 'sit' },
              { who: 'hero', q: 1, r: -1, facing: 2.2, pose: 'sit' },
            ],
            fire: { q: 0, r: 0 },
          },
        },
        {
          // The test: the hairpin bends from the terraces up to the cliff road; the limpets out of the ground at each bend
          // (§3, act 3: curing her is not enough if you don't peel them).
          kind: 'fight',
          id: 'slope',
          mode: 'limpets',
          place: 'lapaSlope',
          weather: 'snow',
          night: true,
          budgetBp: 9000,
          goal: { kind: 'litter' },
          npcs: BEARERS,
          says: [
            { on: 'start', who: 'ludo', line: 'l1', delay: sec(3) },
            { on: 'start', who: 'tessa', line: 'l2', delay: sec(6) },
            { on: 'cue', cue: 'limpet', who: 'tessa', line: 'l3' },
            { on: 'cue', cue: 'leap', who: 'piet', line: 'l4' },
            { on: 'cue', cue: 'down1', who: 'tessa', line: 'l5' },
            { on: 'half', who: 'ludo', line: 'l6' },
          ],
          trial: true,
        },
        {
          kind: 'scene',
          id: 'close',
          lines: [ln('l1', 'tessa'), ln('l2', 'ludo'), ln('l3', 'narrator'), ln('l4', 'narrator'), ln('l5', 'boy'), ln('l6', 'tessa')],
          cine: {
            place: 'slopeTop',
            sky: 'night',
            weather: 'snow',
            cam: [
              { from: [-2.6, 6.2, 2.8], at: [0.4, -0.8, 0.9], ms: 9000 },
              { from: [-0.8, 2.6, 1.1], at: [0.2, 0.1, 0.5], ms: 9000 },
              { from: [1.4, 1.9, 0.9], at: [-0.2, 0.3, 0.4], ms: 9000 },
            ],
            figures: [
              { who: 'tessa', q: 0, r: 0, facing: 0, pose: 'stand' },
              { who: 'ludo', q: -1, r: 0, facing: 0, pose: 'stand' },
              { who: 'piet', q: 1, r: 0, facing: 3.0, pose: 'stand' },
              { who: 'hero', q: 1, r: 1, facing: 4.2, pose: 'stand' },
              { who: 'boy', q: -1, r: 3, facing: 4.6, pose: 'walk', delay: 9000, speed: 30, walkMs: 2600 },
            ],
          },
        },
      ],
    },
    {
      id: 'walkway',
      closedBy: 'tessa',
      piece: 'moonDisc',
      acts: [
        {
          // A full moon over a sea of fog; a walkway of planks nailed to the cliff, over nothing; far below, on the
          // terraces' road, three lanterns coming up behind.
          kind: 'scene',
          id: 'cine',
          lines: [],
          cine: {
            video: 'senda-cleric-2',
            place: 'moonMist',
            sky: 'night',
            // (The board is the walkway of seed 3, engine/pasarela.ts: its second span climbs north-west from (1, 5).)
            cam: [
              { from: [6, 7, 4.2], at: [1, 2, 0.4], ms: 5600 },
              { from: [4, 4, 2.4], at: [0, -1, 0.9], ms: 5400 },
              { from: [-1, 8, 2.6], at: [4, -5, 2.2], ms: 5200 },
            ],
            figures: [
              { who: 'piet', q: 1, r: 2, facing: 2, pose: 'walk', delay: 600, speed: 8, walkMs: 9000 },
              { who: 'tessa', q: 1, r: 3, facing: 2, pose: 'walk', delay: 600, speed: 8, walkMs: 9000 },
              { who: 'ludo', q: 1, r: 4, facing: 2, pose: 'walk', delay: 600, speed: 8, walkMs: 9000 },
            ],
          },
        },
        {
          kind: 'scene',
          id: 'open',
          lines: [ln('l1', 'piet'), ln('l2', 'tessa'), ln('l3', 'ludo')],
          cine: {
            place: 'ridgeFoot',
            sky: 'night',
            weather: 'snow',
            cam: [
              { from: [2.2, 3.4, 2.6], at: [-0.4, -0.6, 0.5], ms: 8000 },
              { from: [-1.0, 2.2, 1.4], at: [0.3, 0.2, 0.5], ms: 8000 },
            ],
            figures: [
              { who: 'tessa', q: 0, r: 0, facing: 0.6, pose: 'stand' },
              { who: 'piet', q: 1, r: -1, facing: 0.3, pose: 'stand' },
              { who: 'ludo', q: -1, r: 1, facing: 0.6, pose: 'stand' },
              { who: 'hero', q: 1, r: 0, facing: 3.4, pose: 'stand' },
            ],
          },
        },
        {
          // A narrow way on the cliff's face, a small chapel now and then; gargoyles come off the rock; goblin slingers on
          // a ledge across the fog, out of reach (§3, act 1).
          kind: 'fight',
          id: 'ridge',
          mode: 'ridge',
          place: 'ridgePath',
          weather: 'snow',
          night: true,
          budgetBp: 9000,
          goal: { kind: 'litter' },
          npcs: [...BEARERS, { id: 'arriero', does: 'wait' as const, hits: 99, vital: false, safe: true }],
          says: [
            { on: 'start', who: 'tessa', line: 'l1', delay: sec(3) },
            { on: 'cue', cue: 'volley1', who: 'piet', line: 'l2' },
            { on: 'cue', cue: 'arriero', who: 'tessa', line: 'l3' },
            { on: 'cue', cue: 'arriero', who: 'arriero', line: 'l4', delay: sec(3) },
            { on: 'cue', cue: 'arriero', who: 'tessa', line: 'l5', delay: sec(6) },
            { on: 'cue', cue: 'down1', who: 'tessa', line: 'l6' },
            { on: 'cue', cue: 'open', who: 'ludo', line: 'l7' },
            { on: 'cue', cue: 'gate', who: 'ludo', line: 'l8' },
          ],
        },
        {
          // Two hundred metres of planks over the fog in four spans; the ink comes up out of it and covers them; the moon,
          // sent by the mirrors, burns it (§3, act 2; §4, «La luna en la pasarela»).
          kind: 'fight',
          id: 'walk',
          mode: 'catwalk',
          place: 'moonWalk',
          night: true,
          budgetBp: 9000,
          goal: { kind: 'litter' },
          npcs: BEARERS,
          says: [
            { on: 'start', who: 'tessa', line: 'l1', delay: sec(2) },
            { on: 'start', who: 'tessa', line: 'l9', delay: sec(8), if: { flag: 'askedFear' } },
            { on: 'cue', cue: 'inscription', who: 'aud', line: 'l2' },
            { on: 'cue', cue: 'dark', who: 'piet', line: 'l3' },
            { on: 'cue', cue: 'fall', who: 'ludo', line: 'l4' },
            { on: 'cue', cue: 'slip', who: 'piet', line: 'l5' },
            { on: 'cue', cue: 'saved', who: 'piet', line: 'l6', delay: sec(1) },
            { on: 'cue', cue: 'saved', who: 'tessa', line: 'l7', delay: sec(4) },
            { on: 'cue', cue: 'gate', who: 'tessa', line: 'l8' },
          ],
        },
        {
          kind: 'fire',
          id: 'fire',
          with: ['piet'],
          questions: [
            { id: 'piet', lines: [ln('l1', 'piet'), ln('l2', 'piet')], set: ['askedPiet'] },
            { id: 'follow', lines: [ln('l1', 'piet'), ln('l2', 'piet')], set: ['askedFollow'] },
            { id: 'after', lines: [ln('l1', 'piet'), ln('l2', 'piet')], set: ['askedAfter'] },
          ],
          cine: {
            place: 'walkFire',
            sky: 'night',
            cam: [
              { from: [2.4, 3.0, 2.0], at: [-0.3, -0.2, 0.35], ms: 9000 },
              { from: [-1.8, 2.8, 1.5], at: [0.2, -0.2, 0.4], ms: 9000 },
            ],
            figures: [
              { who: 'piet', q: 1, r: 0, facing: 3.2, pose: 'sit' },
              { who: 'tessa', q: -1, r: 1, facing: 5.4, pose: 'stand' },
              { who: 'ludo', q: -1, r: -1, facing: 0.6, pose: 'sit' },
              { who: 'hero', q: 0, r: 1, facing: 4.6, pose: 'sit' },
            ],
            fire: { q: 0, r: 0 },
          },
        },
        {
          // The test: the last span, a bridge of planks between two chapels hung from chains; gargoyles tear planks one by
          // one, and a bone mangonel on the far chapel throws at the bridge (§3, act 3).
          kind: 'fight',
          id: 'bridge',
          mode: 'chains',
          place: 'chainBridge',
          night: true,
          budgetBp: 9000,
          goal: { kind: 'litter' },
          npcs: BEARERS,
          says: [
            { on: 'start', who: 'ludo', line: 'l1', delay: sec(2) },
            { on: 'start', who: 'tessa', line: 'l2', delay: sec(8) },
            { on: 'cue', cue: 'stone1', who: 'piet', line: 'l3' },
            { on: 'cue', cue: 'hang', who: 'tessa', line: 'l4' },
            { on: 'cue', cue: 'limpet', who: 'tessa', line: 'l5' },
            { on: 'cue', cue: 'down1', who: 'tessa', line: 'l6' },
            { on: 'half', who: 'ludo', line: 'l7' },
          ],
          trial: true,
        },
        {
          kind: 'scene',
          id: 'close',
          lines: [ln('l1', 'tessa'), ln('l2', 'narrator'), ln('l3', 'narrator'), ln('l4', 'tessa'), ln('l5', 'joran'), ln('l6', 'tessa'), ln('l7', 'narrator'), ln('l8', 'joran')],
          cine: {
            place: 'bridgeChapel',
            sky: 'night',
            cam: [
              { from: [-2.4, 4.6, 2.6], at: [0.3, -0.4, 0.7], ms: 9000 },
              { from: [1.6, 2.4, 1.2], at: [-0.4, -0.6, 0.5], ms: 9000 },
              { from: [-1.2, 1.8, 0.9], at: [0.6, -0.9, 0.45], ms: 9000 },
            ],
            figures: [
              { who: 'tessa', q: 0, r: 1, facing: 4.7, pose: 'stand' },
              { who: 'piet', q: 1, r: 1, facing: 4.5, pose: 'stand' },
              { who: 'ludo', q: -1, r: 2, facing: 4.7, pose: 'stand' },
              { who: 'hero', q: 1, r: 0, facing: 4.4, pose: 'stand' },
              { who: 'joran', q: 0, r: -1, facing: 1.6, pose: 'stand' },
              { who: 'apprenticeA', q: -1, r: -1, facing: 1.3, pose: 'stand' },
              { who: 'apprenticeB', q: 1, r: -2, facing: 1.9, pose: 'stand' },
            ],
          },
        },
      ],
    },
    {
      // Chapter 3, «La nieve» (docs/sendas/clerigo.md §3; battle/nieve.ts): Tessa's cold, the frozen mirrors, the corral.
      id: 'snow',
      closedBy: 'tessa',
      piece: 'furCollar',
      acts: [
        {
          // The blizzard comes in from the left and hides everything. Two lanterns in the snow: the stretcher's, and a
          // few steps behind, Joran's, who neither comes closer nor goes.
          kind: 'scene',
          id: 'cine',
          lines: [],
          cine: {
            video: 'senda-cleric-3',
            place: 'snowStorm',
            sky: 'night',
            weather: 'snow',
            cam: [
              { from: [5.0, 3.0, 2.6], at: [0, 0, 0.5], ms: 5600 },
              { from: [2.4, 1.0, 1.4], at: [-0.6, -0.4, 0.5], ms: 5400 },
              { from: [-1.6, 3.4, 1.2], at: [0.6, -1.6, 0.6], ms: 5200 },
            ],
            figures: [
              { who: 'piet', q: 0, r: 1, facing: 4.7, pose: 'walk', delay: 500, speed: 6, walkMs: 12000 },
              { who: 'tessa', q: 0, r: 2, facing: 4.7, pose: 'walk', delay: 500, speed: 6, walkMs: 12000 },
              { who: 'ludo', q: 0, r: 3, facing: 4.7, pose: 'walk', delay: 500, speed: 6, walkMs: 12000 },
              { who: 'joran', q: 1, r: 5, facing: 4.7, pose: 'stand' },
            ],
          },
        },
        {
          kind: 'scene',
          id: 'open',
          lines: [ln('l1', 'joran'), ln('l2', 'tessa'), ln('l3', 'joran')],
          cine: {
            place: 'colFoot',
            sky: 'night',
            weather: 'snow',
            cam: [
              { from: [2.4, 3.6, 2.4], at: [-0.2, -0.4, 0.5], ms: 8000 },
              { from: [-1.4, 2.4, 1.3], at: [0.3, 0.1, 0.5], ms: 8000 },
            ],
            figures: [
              { who: 'tessa', q: 0, r: 0, facing: 1.6, pose: 'stand' },
              { who: 'piet', q: 1, r: -1, facing: 1.9, pose: 'stand' },
              { who: 'ludo', q: -1, r: 0, facing: 1.2, pose: 'stand' },
              { who: 'hero', q: 1, r: 0, facing: 2.6, pose: 'stand' },
              { who: 'joran', q: 0, r: 2, facing: 4.7, pose: 'stand' },
              { who: 'apprenticeA', q: -1, r: 3, facing: 4.6, pose: 'stand' },
              { who: 'apprenticeB', q: 1, r: 2, facing: 4.8, pose: 'stand' },
            ],
          },
        },
        {
          // The white col in the blizzard: Tessa has cold (a bar that always drops, faster with the stretcher still);
          // the braziers in the chapels bring it up; wolves out of the snow; Joran and his apprentices behind (§3, act 1).
          kind: 'fight',
          id: 'col',
          mode: 'col',
          place: 'snowCol',
          weather: 'snow',
          night: true,
          budgetBp: 9000,
          goal: { kind: 'litter' },
          npcs: [
            ...BEARERS,
            { id: 'joran', does: 'wait' as const, hits: 99, vital: false, safe: true },
            { id: 'apprenticeA', does: 'wait' as const, hits: 99, vital: false, safe: true },
            { id: 'apprenticeB', does: 'wait' as const, hits: 99, vital: false, safe: true },
          ],
          says: [
            { on: 'start', who: 'tessa', line: 'l1', delay: sec(3) },
            { on: 'cue', cue: 'blue', who: 'piet', line: 'l2' },
            { on: 'cue', cue: 'blue', who: 'joran', line: 'l3', delay: sec(3) },
            { on: 'cue', cue: 'lit1', who: 'tessa', line: 'l4', delay: sec(1) },
            { on: 'cue', cue: 'wolf', who: 'ludo', line: 'l5' },
            { on: 'cue', cue: 'down1', who: 'tessa', line: 'l6' },
            { on: 'half', who: 'joran', line: 'l7' },
            { on: 'cue', cue: 'gate', who: 'piet', line: 'l8' },
          ],
        },
        {
          // A wide terrace with the snow chapel's mirrors, all under frost: three braziers for six bases, each thaws the
          // mirrors stuck to it; one alone reaches the stretcher (§3, act 2; §4, «Los espejos helados»).
          kind: 'fight',
          id: 'frost',
          mode: 'frost',
          place: 'frostTerrace',
          weather: 'snow',
          night: true,
          budgetBp: 9000,
          goal: { kind: 'lamp' },
          npcs: BEARERS,
          says: [
            { on: 'start', who: 'tessa', line: 'l1', delay: sec(2) },
            { on: 'start', who: 'tessa', line: 'l9', delay: sec(7), if: { flag: 'askedHermitage' } },
            { on: 'cue', cue: 'inscription', who: 'aud', line: 'l2' },
            { on: 'cue', cue: 'iced', who: 'piet', line: 'l3' },
            { on: 'cue', cue: 'thaw1', who: 'tessa', line: 'l4', delay: sec(1) },
            { on: 'cue', cue: 'stray', who: 'ludo', line: 'l5' },
            { on: 'cue', cue: 'shiver', who: 'piet', line: 'l6' },
            { on: 'cue', cue: 'lit', who: 'tessa', line: 'l7' },
            { on: 'cue', cue: 'down1', who: 'tessa', line: 'l8' },
          ],
        },
        {
          // In the stone corral of Piet's parents, with Tessa and Ludo: the chapter's big scene; then, alone with the
          // cleric and Piet, the hidden truth (§3, the campfire).
          kind: 'fire',
          id: 'fire',
          with: ['tessa', 'ludo'],
          questions: [
            {
              id: 'listen',
              lines: [ln('l1', 'tessa'), ln('l2', 'ludo'), ln('l3', 'tessa'), ln('l4', 'ludo'), ln('l5', 'tessa'), ln('l6', 'tessa'), ln('l7', 'ludo')],
              set: ['askedLudoListen'],
            },
            {
              id: 'joran',
              lines: [ln('l1', 'tessa'), ln('l2', 'tessa'), ln('l3', 'piet'), ln('l4', 'tessa'), ln('l5', 'tessa')],
              set: ['askedJoran'],
            },
            { id: 'fear', lines: [ln('l0', 'tessa', { flag: 'askedFear' }), ln('l1', 'tessa'), ln('l2', 'tessa')], set: ['askedFearUp'] },
          ],
          cine: {
            place: 'corralFire',
            sky: 'night',
            cam: [
              { from: [2.4, 3.0, 2.0], at: [-0.3, -0.2, 0.35], ms: 9000 },
              { from: [-1.8, 2.8, 1.5], at: [0.2, -0.2, 0.4], ms: 9000 },
            ],
            figures: [
              { who: 'tessa', q: -1, r: 1, facing: 5.4, pose: 'stand' },
              { who: 'ludo', q: -1, r: 0, facing: 0.3, pose: 'sit' },
              { who: 'piet', q: 1, r: 0, facing: 3.2, pose: 'sit' },
              { who: 'hero', q: 0, r: 1, facing: 4.6, pose: 'sit' },
            ],
            fire: { q: 0, r: 0 },
          },
        },
        {
          // The test: Piet's parents' corral in the blizzard, a night of seven minutes on the clock; moths of sleep for
          // the braziers, wolves through the gaps in the wall, and the ash watcher out of the storm's sky (§3, act 3).
          kind: 'fight',
          id: 'corral',
          mode: 'corral',
          place: 'corralNight',
          weather: 'snow',
          night: true,
          budgetBp: 9000,
          goal: { kind: 'night' },
          npcs: [
            ...BEARERS,
            { id: 'pietMother', does: 'wait' as const, hits: 3, vital: false, safe: true },
            { id: 'kidA', does: 'wait' as const, hits: 3, vital: false, safe: true },
            { id: 'kidB', does: 'wait' as const, hits: 3, vital: false, safe: true },
          ],
          says: [
            { on: 'start', who: 'pietMother', line: 'l1', delay: sec(3) },
            { on: 'start', who: 'tessa', line: 'l2', delay: sec(8) },
            { on: 'cue', cue: 'shadow1', who: 'piet', line: 'l3' },
            { on: 'cue', cue: 'took', who: 'tessa', line: 'l4', delay: sec(1) },
            { on: 'cue', cue: 'frozen1', who: 'pietMother', line: 'l5' },
            { on: 'cue', cue: 'moth', who: 'piet', line: 'l6' },
            { on: 'half', who: 'ludo', line: 'l7' },
            { on: 'cue', cue: 'down1', who: 'tessa', line: 'l8' },
            { on: 'cue', cue: 'fell', who: 'piet', line: 'l9' },
            { on: 'cue', cue: 'dawn', who: 'tessa', line: 'l10' },
          ],
          trial: true,
        },
        {
          kind: 'scene',
          id: 'close',
          lines: [ln('l1', 'ludo'), ln('l2', 'piet'), ln('l3', 'narrator'), ln('l4', 'narrator'), ln('l5', 'tessa', { notFlag: 'askedJoran' }), ln('l6', 'joran')],
          cine: {
            place: 'corralDawn',
            sky: 'night',
            weather: 'snow',
            cam: [
              { from: [-2.4, 4.6, 2.6], at: [0.3, -0.4, 0.7], ms: 9000 },
              { from: [1.6, 2.4, 1.2], at: [-0.4, -0.6, 0.5], ms: 9000 },
              { from: [-1.2, 1.8, 0.9], at: [0.6, -0.9, 0.45], ms: 9000 },
            ],
            figures: [
              { who: 'tessa', q: 0, r: 0, facing: 1.6, pose: 'stand' },
              { who: 'ludo', q: -1, r: 0, facing: 0.4, pose: 'stand' },
              { who: 'piet', q: 1, r: 0, facing: 2.8, pose: 'stand' },
              { who: 'hero', q: 1, r: 1, facing: 4.2, pose: 'stand' },
              { who: 'joran', q: -2, r: 3, facing: 5.2, pose: 'stand' },
              { who: 'apprenticeA', q: -3, r: 3, facing: 5.6, pose: 'sit' },
              { who: 'apprenticeB', q: -2, r: 4, facing: 5.0, pose: 'sit' },
            ],
          },
        },
      ],
    },
    {
      // Chapter 4, «La Ermita» (docs/sendas/clerigo.md §3; battle/ermita.ts): the Hermitage of the Crag broken, Ludo gone
      // with the silver, the decision; the atrium, Aud's last vigil remembered, the nave of the mirrors.
      id: 'hermitage',
      closedBy: 'tessa',
      piece: 'audWick',
      acts: [
        {
          // At dawn, the Hermitage of the Crag: a chapel cut in the cliff, a tower out over nothing, a great lantern at its
          // top, dark. The nave's roof broken. Ink on the walls.
          kind: 'scene',
          id: 'cine',
          lines: [],
          cine: {
            video: 'senda-cleric-4',
            place: 'ermitaDawn',
            sky: 'dawn',
            weather: 'snow',
            cam: [
              // Far down the way: the cliff, the Hermitage cut in it, the tower over nothing.
              { from: [-2.0, 9.0, 4.2], at: [1.2, -3.2, 4.6], ms: 5600 },
              // Up to the tower and its great lantern, dark.
              { from: [1.2, 3.8, 3.0], at: [5.4, -4.0, 10.5], ms: 5400 },
              // The door, broken; ink down the face.
              { from: [0.8, 3.0, 1.4], at: [0, -2.6, 2.4], ms: 5200 },
            ],
            figures: [
              { who: 'piet', q: -1, r: 4, facing: 1.5, pose: 'walk', delay: 500, speed: 8, walkMs: 7000 },
              { who: 'tessa', q: -1, r: 5, facing: 1.5, pose: 'walk', delay: 500, speed: 8, walkMs: 7000 },
              { who: 'ludo', q: -2, r: 6, facing: 1.5, pose: 'walk', delay: 500, speed: 8, walkMs: 7000 },
            ],
          },
        },
        {
          // The lowest point (no fight): the Hermitage broken; Ludo sets his pole down and goes with the silver; Joran at
          // the broken door; Tessa, her eyes shut, asks if it is as it was.
          kind: 'scene',
          id: 'open',
          lines: [ln('l1', 'piet'), ln('l2', 'ludo'), ln('l3', 'ludo'), ln('l4', 'piet'), ln('l5', 'narrator'), ln('l6', 'joran'), ln('l7', 'joran'), ln('l8', 'tessa')],
          cine: {
            place: 'ermitaDoor',
            sky: 'dawn',
            weather: 'snow',
            cam: [
              { from: [2.6, 4.2, 2.6], at: [-0.2, -1.2, 0.8], ms: 9000 },
              { from: [-1.6, 2.6, 1.3], at: [0.4, -0.4, 0.6], ms: 9000 },
              { from: [1.2, 1.4, 0.9], at: [-0.6, 3.4, 0.5], ms: 9000 },
            ],
            figures: [
              { who: 'tessa', q: 0, r: 0, facing: 1.6, pose: 'stand' },
              { who: 'piet', q: 1, r: -1, facing: 2.0, pose: 'stand' },
              { who: 'ludo', q: -1, r: 1, facing: 4.5, pose: 'walk', delay: 9500, speed: 14, walkMs: 9000 },
              { who: 'hero', q: 1, r: 0, facing: 2.6, pose: 'stand' },
              { who: 'joran', q: 0, r: -2, facing: 4.5, pose: 'stand' },
              { who: 'apprenticeA', q: -1, r: -2, facing: 4.4, pose: 'stand' },
              { who: 'apprenticeB', q: 1, r: -2, facing: 4.6, pose: 'stand' },
            ],
          },
        },
        {
          // The decision, looking at the ruin, with Piet alone at the stretcher (§6).
          kind: 'choice',
          id: 'choice',
          lines: [ln('l1', 'piet'), ln('l2', 'tessa')],
          options: [
            { id: 'truth', set: ['truth'] },
            { id: 'believe', set: ['believe'] },
          ],
          cine: {
            place: 'ermitaDoor',
            sky: 'dawn',
            weather: 'snow',
            cam: [
              { from: [1.6, 2.2, 1.4], at: [-0.2, -0.6, 0.6], ms: 9000 },
              { from: [-1.2, 1.6, 1.0], at: [0.2, -0.2, 0.5], ms: 9000 },
            ],
            figures: [
              { who: 'tessa', q: 0, r: 0, facing: 1.6, pose: 'stand' },
              { who: 'piet', q: 1, r: -1, facing: 2.0, pose: 'stand' },
              { who: 'hero', q: 1, r: 0, facing: 2.6, pose: 'stand' },
            ],
          },
        },
        {
          // The atrium, the cells, the refectory: a descent cut in the rock, without the stretcher (it stays outside with
          // Piet by a brazier; Joran and his apprentices wait at the door). Skeletons of pilgrims no one buried, a bone
          // singer, mourners that mend the rest (§3, act 1).
          kind: 'fight',
          id: 'atrium',
          mode: 'descent',
          place: 'ermitaAtrium',
          rooms: 3,
          budgetBp: 7000,
          bestiary: { skeleton: 4, mourner: 1, bonesinger: 1 },
          goal: { kind: 'reach' },
          says: [
            { on: 'start', who: 'aud', line: 'l1', delay: sec(3) },
            { on: 'at', t: sec(40), who: 'piet', line: 'l2' },
            { on: 'at', t: sec(110), who: 'joran', line: 'l3' },
          ],
        },
        {
          // The memory (docs/SENDAS.md, emotion, piece 9): in the nave, behind the altar, the empty place of a mirror and a
          // stub of wick. Touched, the scene goes grey and the hero is Madre Aud, sixty-two years before: the night Tessa's
          // mother goes. Tessa at twenty-two holds her hand at the altar (§3, act 2).
          kind: 'fight',
          id: 'vigil',
          mode: 'audVigil',
          place: 'naveMemory',
          night: true,
          memory: 'audPast',
          budgetBp: 9000,
          goal: { kind: 'dawn' },
          npcs: [
            { id: 'tessaYoung', does: 'wait' as const, hits: 99, vital: false, safe: true },
            { id: 'tessaMother', does: 'wait' as const, hits: 99, vital: false, safe: true },
          ],
          says: [
            { on: 'start', who: 'audPast', line: 'l1', delay: sec(3) },
            { on: 'cue', cue: 'in', who: 'tessaYoung', line: 'l2' },
            { on: 'cue', cue: 'in', who: 'audPast', line: 'l3', delay: sec(3) },
            { on: 'cue', cue: 'wail1', who: 'tessaYoung', line: 'l4' },
            { on: 'cue', cue: 'hush', who: 'audPast', line: 'l5' },
            { on: 'cue', cue: 'snuffed', who: 'tessaYoung', line: 'l6' },
            { on: 'cue', cue: 'lamp3', who: 'audPast', line: 'l7' },
            { on: 'half', who: 'tessaMother', line: 'l8' },
            { on: 'cue', cue: 'lamp6', who: 'tessaYoung', line: 'l9' },
          ],
        },
        {
          // Won: the dawn, in the memory. The sun comes in by the roof, whole then with its opening, crosses the mirrors
          // and reaches the face of the one who goes.
          kind: 'scene',
          id: 'dawnPast',
          lines: [ln('l1', 'audPast'), ln('l2', 'audPast'), ln('l3', 'audPast'), ln('l4', 'narrator')],
          cine: {
            place: 'navePast',
            sky: 'dawn',
            memory: true,
            // (The nave of NAVE_SCENE_SEED, engine/ermita.ts: the altar at (3, −7), the door at (−5, 7).)
            cam: [
              { from: [3.6, -3.2, 3.0], at: [3, -7, 0.8], ms: 9000 },
              { from: [1.6, -4.6, 1.4], at: [3, -6.8, 0.7], ms: 9000 },
              { from: [1.8, -2.4, 1.1], at: [0, -4, 0.45], ms: 9000 },
            ],
            figures: [
              { who: 'tessaMother', q: 3, r: -7, facing: 0, pose: 'stand' },
              { who: 'tessaYoung', q: 2.2, r: -6.1, facing: 1.2, pose: 'kneel' },
              { who: 'audPast', q: 0, r: -4, facing: 0.4, pose: 'sit' },
            ],
          },
        },
        {
          // The present again: Aud's mirror where she hid it, wrapped in a rag, behind a loose stone.
          kind: 'scene',
          id: 'stone',
          lines: [ln('l1', 'narrator'), ln('l2', 'narrator')],
          cine: {
            place: 'naveNow',
            sky: 'dawn',
            cam: [
              { from: [4.8, -4.2, 1.8], at: [4.1, -7.1, 0.6], ms: 9000 },
              { from: [3.4, -5.3, 1.0], at: [4.2, -7.1, 0.55], ms: 9000 },
            ],
            figures: [{ who: 'hero', q: 3.9, r: -6.3, facing: 1.4, pose: 'kneel' }],
          },
        },
        {
          // In the refectory, with Tessa, whom Piet carried in on his back.
          kind: 'fire',
          id: 'fire',
          with: ['tessa', 'piet'],
          questions: [
            { id: 'mother', lines: [ln('l1', 'tessa'), ln('l2', 'tessa')], set: ['askedMother'] },
            { id: 'lamp', lines: [ln('l1', 'tessa')], set: ['askedLamp'] },
            { id: 'joranHere', lines: [ln('l1', 'tessa'), ln('l2', 'tessa')], set: ['askedJoranHere'] },
          ],
          cine: {
            place: 'refectory',
            sky: 'night',
            cam: [
              { from: [2.4, 3.0, 2.0], at: [-0.3, -0.2, 0.35], ms: 9000 },
              { from: [-1.8, 2.8, 1.5], at: [0.2, -0.2, 0.4], ms: 9000 },
            ],
            figures: [
              { who: 'tessa', q: -1, r: 1, facing: 5.4, pose: 'stand' },
              { who: 'piet', q: 1, r: 0, facing: 3.2, pose: 'sit' },
              { who: 'hero', q: 0, r: 1, facing: 4.6, pose: 'sit' },
            ],
            fire: { q: 0, r: 0 },
          },
        },
        {
          // The test: the nave, six great mirrors and two dividers of glass; the lantern hung under the hole in the roof.
          // The way of the light for the dawn, to the altar and the door at once; limpets out of the ink on the walls and the
          // great bone singer (§3, act 3; §4, «La nave de los espejos»).
          kind: 'fight',
          id: 'nave',
          mode: 'nave',
          place: 'naveNow',
          night: true,
          budgetBp: 9000,
          goal: { kind: 'mirrors' },
          npcs: [
            { id: 'piet', does: 'wait' as const, hits: 6, vital: false },
            { id: 'tessa', does: 'wait' as const, hits: 20, model: 'tessaLitter' },
          ],
          says: [
            { on: 'start', who: 'tessa', line: 'l1', delay: sec(2), if: TRUTH },
            { on: 'start', who: 'tessa', line: 'l2', delay: sec(2), if: BELIEVE },
            { on: 'cue', cue: 'set', who: 'aud', line: 'l3' },
            { on: 'cue', cue: 'altarOnly', who: 'piet', line: 'l4' },
            { on: 'cue', cue: 'weak', who: 'tessa', line: 'l5' },
            { on: 'cue', cue: 'limpet', who: 'piet', line: 'l6' },
            { on: 'cue', cue: 'covered', who: 'piet', line: 'l7' },
            { on: 'cue', cue: 'down1', who: 'tessa', line: 'l8' },
            { on: 'half', who: 'tessa', line: 'l9', if: { flag: 'askedLamp' } },
            { on: 'cue', cue: 'lit', who: 'tessa', line: 'l10' },
          ],
          trial: true,
          // The nave of the try won (its seed) keeps for chapter 5: the same mirrors, Aud's in its place.
          keep: ['nave'],
        },
        {
          kind: 'scene',
          id: 'close',
          lines: [
            ln('l1', 'tessa', TRUTH),
            ln('l2', 'tessa', TRUTH),
            ln('l3', 'narrator', BELIEVE),
            ln('l4', 'tessa', BELIEVE),
            ln('l5', 'piet', BELIEVE),
            ln('l6', 'narrator'),
            ln('l7', 'joran'),
            ln('l8', 'narrator'),
          ],
          cine: {
            place: 'naveClose',
            sky: 'night',
            cam: [
              { from: [3.4, -1.6, 3.4], at: [2.6, -6.2, 0.7], ms: 9000 },
              { from: [-2.2, 2.6, 2.6], at: [-4.8, 6.8, 0.9], ms: 9000 },
              { from: [3.4, 2.6, 2.6], at: [2.0, -1.0, 4.5], ms: 9000 },
            ],
            figures: [
              { who: 'tessa', q: 2.6, r: -6.2, facing: 1.5, pose: 'stand' },
              { who: 'piet', q: 2, r: -5, facing: 0.6, pose: 'stand' },
              { who: 'hero', q: 1, r: -5, facing: 1.0, pose: 'stand' },
              { who: 'joran', q: -4.6, r: 6.4, facing: 1.5, pose: 'stand' },
            ],
          },
        },
      ],
    },
    {
      // Chapter 5, «La vigilia» (docs/sendas/clerigo.md §3; battle/vigilia.ts): the Hermitage by night, Tessa on the altar,
      // the candle by her. The lamps of the vigil, the procession stopped in the atrium, the letters, the mirrors set again,
      // the speech, the Doliente until the sun; Tessa dies at dawn with her son's hand in hers.
      id: 'vigil',
      closedBy: 'tessa',
      piece: 'tessaStole',
      call: true,
      // Lost to the Doliente, a closed chapter opens again at her fight: the lamps, the atrium and the mirrors stay won.
      keep: 'mourn',
      acts: [
        {
          // The Hermitage by night. Tessa on the altar, on a blanket; a candle on a stone by her. Aud's mirror back in its
          // place behind the altar; above, the black hole of the broken roof. In a niche, in a row, the clay lamps of the
          // vigil. Joran takes one without anyone giving it to him. Far off, in the snow, something drags a veil.
          kind: 'scene',
          id: 'cine',
          lines: [],
          cine: {
            video: 'senda-cleric-5',
            place: 'naveVigil',
            sky: 'night',
            weather: 'snow',
            cam: [
              // Down through the hole in the roof, the snow coming in, to the altar and the candle.
              { from: [2.0, -1.0, 7.5], at: [3.0, -6.6, 0.6], ms: 6000 },
              // The niche of the lamps; Joran's hand.
              { from: [0.6, -3.4, 1.6], at: [-0.6, -5.6, 0.9], ms: 5600 },
              // Out by the door: the snow on the way down, something long and black far off.
              { from: [-3.6, 4.6, 1.8], at: [-6.4, 10.5, 0.6], ms: 5600 },
            ],
            figures: [
              { who: 'tessa', q: 3, r: -6.4, facing: 1.5, pose: 'lie' },
              { who: 'joran', q: 0.2, r: -5.2, facing: 2.4, pose: 'stand' },
              { who: 'piet', q: 2, r: -5.4, facing: 0.6, pose: 'kneel' },
            ],
          },
        },
        {
          // The nave, Tessa on the altar; if asked at the crossroads whether she was afraid, she answers it now.
          kind: 'scene',
          id: 'open',
          lines: [ln('l1', 'tessa', { flag: 'askedFear' }), ln('l2', 'tessa', { flag: 'askedFear' }), ln('l3', 'tessa')],
          cine: {
            place: 'naveVigil',
            sky: 'night',
            weather: 'snow',
            cam: [
              { from: [4.2, -4.4, 1.5], at: [3.0, -6.6, 0.55], ms: 9000 },
              { from: [1.6, -5.0, 1.2], at: [3.2, -6.6, 0.5], ms: 9000 },
            ],
            figures: [
              { who: 'tessa', q: 3, r: -6.4, facing: 1.5, pose: 'lie' },
              { who: 'hero', q: 2.1, r: -5.6, facing: 0.4, pose: 'kneel' },
              { who: 'piet', q: 3.8, r: -5.6, facing: 2.6, pose: 'stand' },
              { who: 'joran', q: 0.4, r: -4.6, facing: 0.8, pose: 'stand' },
            ],
          },
        },
        {
          // Act 1: the lamps of the vigil lit as Aud did, one for each hour (one a minute), from the door to the altar,
          // while the nightmares come down by the broken roof and in by the cells (§3, act 1).
          kind: 'fight',
          id: 'lamps',
          mode: 'lamps',
          place: 'naveVigil',
          night: true,
          weather: 'snow',
          budgetBp: 9000,
          goal: { kind: 'lamps' },
          npcs: [
            { id: 'tessa', does: 'wait' as const, hits: 99, vital: false, safe: true, model: 'tessaLitter' },
            { id: 'piet', does: 'wait' as const, hits: 99, vital: false, safe: true },
            { id: 'joran', does: 'wait' as const, hits: 99, vital: false, safe: true },
          ],
          says: [
            { on: 'start', who: 'tessa', line: 'l1', delay: sec(3) },
            { on: 'cue', cue: 'lamp1', who: 'piet', line: 'l2', delay: sec(1) },
            { on: 'cue', cue: 'lamp3', who: 'joran', line: 'l3', delay: sec(1) },
            { on: 'cue', cue: 'roof', who: 'piet', line: 'l4' },
            { on: 'cue', cue: 'snuffed', who: 'tessa', line: 'l5' },
            { on: 'cue', cue: 'lamp6', who: 'tessa', line: 'l6', delay: sec(1) },
          ],
        },
        {
          // Act 2: the Doliente at the door sends first what she brings: a procession in by the atrium. Joran and his
          // apprentices are in the middle and fight beside the hero; Piet bars the nave's door (§3, act 2).
          kind: 'fight',
          id: 'atrium',
          mode: 'procession',
          place: 'atriumNight',
          night: true,
          budgetBp: 9000,
          goal: { kind: 'procession' },
          npcs: [
            { id: 'piet', does: 'wait' as const, hits: 99, vital: false, safe: true },
            { id: 'joran', does: 'wait' as const, hits: 10, vital: false },
            { id: 'apprenticeA', does: 'wait' as const, hits: 7, vital: false },
            { id: 'apprenticeB', does: 'wait' as const, hits: 7, vital: false },
          ],
          says: [
            { on: 'start', who: 'joran', line: 'l1', delay: sec(5) },
            { on: 'cue', cue: 'singer', who: 'piet', line: 'l2' },
            { on: 'cue', cue: 'door', who: 'piet', line: 'l3' },
            { on: 'cue', cue: 'joranDown', who: 'apprenticeA', line: 'l4' },
            { on: 'half', who: 'joran', line: 'l5' },
            { on: 'cue', cue: 'last', who: 'piet', line: 'l6' },
          ],
        },
        {
          // In the nave, after the atrium: Piet speaks to Joran, as Tessa told him to, if anything happened.
          kind: 'scene',
          id: 'letters',
          lines: [ln('l1', 'piet'), ln('l2', 'piet'), ln('l3', 'joran'), ln('l4', 'joran'), ln('l5', 'narrator')],
          cine: {
            place: 'naveLit',
            sky: 'night',
            weather: 'snow',
            cam: [
              { from: [-0.4, -2.4, 1.4], at: [1.2, -4.6, 0.8], ms: 9000 },
              { from: [3.4, -3.6, 1.2], at: [1.4, -4.6, 0.8], ms: 9000 },
              { from: [4.0, -5.0, 1.3], at: [3.0, -6.5, 0.5], ms: 9000 },
            ],
            figures: [
              { who: 'tessa', q: 3, r: -6.4, facing: 1.5, pose: 'lie' },
              { who: 'piet', q: 1.6, r: -4.2, facing: 3.4, pose: 'stand' },
              { who: 'joran', q: 0.7, r: -4.6, facing: 0.2, pose: 'stand' },
              { who: 'hero', q: 2.4, r: -3.6, facing: 2.4, pose: 'stand' },
            ],
          },
        },
        {
          // The campfire in the nave: Joran, Tessa and Piet; the brazier by the altar.
          kind: 'fire',
          id: 'fire',
          with: ['joran', 'tessa', 'piet'],
          questions: [
            { id: 'letters', lines: [ln('l1', 'joran'), ln('l2', 'joran')], set: ['askedLetters'] },
            { id: 'stay', lines: [ln('l1', 'joran'), ln('l2', 'joran')] },
            { id: 'ready', lines: [ln('l1', 'tessa'), ln('l2', 'tessa')], set: ['askedReady'] },
          ],
          cine: {
            place: 'naveLit',
            sky: 'night',
            weather: 'snow',
            cam: [
              { from: [0.2, -2.6, 1.8], at: [1.6, -5.2, 0.4], ms: 9000 },
              { from: [3.8, -3.4, 1.5], at: [1.8, -5.2, 0.45], ms: 9000 },
            ],
            figures: [
              { who: 'tessa', q: 3, r: -6.4, facing: 1.5, pose: 'lie' },
              { who: 'joran', q: 1.0, r: -5.4, facing: 0.4, pose: 'sit' },
              { who: 'piet', q: 2.6, r: -4.6, facing: 3.6, pose: 'sit' },
              { who: 'hero', q: 1.2, r: -4.0, facing: 5.2, pose: 'sit' },
            ],
            fire: { q: 1.8, r: -4.9 },
          },
        },
        {
          // The test: before dawn the wind moved three of the nave's mirrors; the way of the light set again, to the altar
          // and the door, with limpets on the glass (§3, act 3; §4, «Los espejos otra vez»).
          kind: 'fight',
          id: 'mirrors',
          mode: 'renave',
          place: 'naveLit',
          night: true,
          weather: 'snow',
          budgetBp: 9000,
          goal: { kind: 'mirrors' },
          npcs: [
            { id: 'piet', does: 'wait' as const, hits: 6, vital: false },
            { id: 'tessa', does: 'wait' as const, hits: 20, model: 'tessaLitter' },
            { id: 'joran', does: 'wait' as const, hits: 99, vital: false, safe: true },
          ],
          says: [
            { on: 'start', who: 'piet', line: 'l1', delay: sec(2) },
            { on: 'cue', cue: 'limpet', who: 'piet', line: 'l2' },
            { on: 'cue', cue: 'altarOnly', who: 'joran', line: 'l3' },
            { on: 'cue', cue: 'weak', who: 'tessa', line: 'l4' },
            { on: 'cue', cue: 'covered', who: 'piet', line: 'l5' },
            { on: 'half', who: 'tessa', line: 'l6', if: { flag: 'askedReady' } },
            { on: 'cue', cue: 'lit', who: 'tessa', line: 'l7' },
          ],
          trial: true,
        },
        {
          // The speech (docs/SENDAS.md, emotion, piece 7): Tessa's voice is gone; she speaks in Piet's ear and Piet says it
          // aloud at the door, to Joran and the apprentices. They raise the lamps of the vigil, one by one.
          kind: 'choice',
          id: 'speech',
          lines: [
            ln('l1', 'piet'),
            { id: 'l2', who: 'narrator', cue: 'raise1' },
            ln('l3', 'piet'),
            { id: 'l4', who: 'narrator', cue: 'raise2' },
            ln('l5', 'piet'),
            ln('l6', 'joran'),
            ln('l7', 'piet'),
            { id: 'l8', who: 'narrator', cue: 'raise3' },
          ],
          options: [
            { id: 'dawn', set: ['speechDawn'] },
            { id: 'seen', set: ['speechSeen'] },
          ],
          cine: {
            place: 'naveLit',
            sky: 'night',
            weather: 'snow',
            cam: [
              // At the door with Piet, Tessa far up the nave on her altar; then the lamps raised, one by one.
              { from: [-2.6, 4.4, 1.3], at: [-4.6, 6.4, 0.9], ms: 12000 },
              { from: [-3.0, 3.4, 1.0], at: [-4.8, 6.6, 1.0], ms: 14000 },
              { from: [-1.8, 3.0, 1.9], at: [-5.0, 6.8, 1.1], ms: 14000 },
            ],
            figures: [
              { who: 'piet', q: -4.4, r: 5.6, facing: 1.6, pose: 'stand' },
              { who: 'joran', q: -5.4, r: 6.4, facing: 4.6, pose: 'stand' },
              { who: 'apprenticeA', q: -4.6, r: 6.8, facing: 4.4, pose: 'stand' },
              { who: 'apprenticeB', q: -3.6, r: 6.6, facing: 4.8, pose: 'stand' },
              { who: 'hero', q: -3.4, r: 5.4, facing: 4.4, pose: 'stand' },
              { who: 'tessa', q: 3, r: -6.4, facing: 1.5, pose: 'lie' },
            ],
          },
        },
        {
          // Act 4: the Doliente, until the sun (§5). The ones who come: at the third minute, Ludo and the muleteers in by
          // the atrium with their mules and skins of vinegar.
          kind: 'fight',
          id: 'mourn',
          mode: 'mourn',
          place: 'naveVigil',
          night: true,
          weather: 'snow',
          budgetBp: 9000,
          goal: { kind: 'mourn' },
          npcs: [
            { id: 'tessa', does: 'wait' as const, hits: 20, model: 'tessaLitter' },
            { id: 'piet', does: 'wait' as const, hits: 8, vital: false },
            { id: 'joran', does: 'wait' as const, hits: 10, vital: false },
            { id: 'apprenticeA', does: 'wait' as const, hits: 8, vital: false },
            { id: 'apprenticeB', does: 'wait' as const, hits: 8, vital: false },
            { id: 'ludo', does: 'wait' as const, hits: 10, vital: false },
            { id: 'arriero', does: 'wait' as const, hits: 8, vital: false },
            { id: 'arrieroB', does: 'wait' as const, hits: 8, vital: false },
          ],
          says: [
            { on: 'start', who: 'joran', line: 'l1', delay: sec(3), if: TRUTH },
            { on: 'start', who: 'piet', line: 'l2', delay: sec(3), if: BELIEVE },
            { on: 'cue', cue: 'wail1', who: 'piet', line: 'l3' },
            { on: 'cue', cue: 'small', who: 'tessa', line: 'l4' },
            { on: 'cue', cue: 'hold', who: 'joran', line: 'l5' },
            { on: 'cue', cue: 'split', who: 'piet', line: 'l6' },
            { on: 'cue', cue: 'come', who: 'ludo', line: 'l7' },
            { on: 'cue', cue: 'come', who: 'ludo', line: 'l8', delay: sec(4) },
            { on: 'cue', cue: 'come', who: 'arriero', line: 'l9', delay: sec(8) },
            { on: 'cue', cue: 'frost', who: 'piet', line: 'l10' },
            { on: 'cue', cue: 'sun', who: 'tessa', line: 'l11' },
            { on: 'cue', cue: 'sunMiss', who: 'joran', line: 'l12' },
            { on: 'cue', cue: 'hunt', who: 'piet', line: 'l13', delay: sec(2) },
          ],
          foe: true,
          trial: true,
        },
        {
          // The sun in through the broken roof, across the mirrors and onto Tessa's face, her son's hand in hers.
          kind: 'scene',
          id: 'close',
          lines: [
            ln('l1', 'tessa'),
            ln('l2', 'tessa'),
            ln('l9', 'narrator'),
            ln('l3', 'tessa', TRUTH),
            ln('l4', 'tessa', BELIEVE),
            ln('l5', 'joran'),
            ln('l6', 'narrator'),
            ln('l7', 'piet'),
            ln('l8', 'ludo'),
          ],
          cine: {
            place: 'naveDawn',
            sky: 'dawn',
            cam: [
              // Down the shaft of sun from the hole in the roof, then across the mirrors to the altar.
              { from: [1.4, -0.6, 6.4], at: [3.0, -6.4, 0.7], ms: 10000 },
              { from: [4.2, -4.8, 1.3], at: [3.0, -6.6, 0.55], ms: 10000 },
              { from: [-1.6, 1.6, 1.5], at: [1.6, -4.6, 0.7], ms: 10000 },
            ],
            figures: [
              { who: 'tessa', q: 3, r: -6.4, facing: 1.5, pose: 'lie' },
              { who: 'joran', q: 2.2, r: -5.7, facing: 0.6, pose: 'kneel' },
              { who: 'piet', q: 3.9, r: -5.6, facing: 2.6, pose: 'stand' },
              { who: 'hero', q: 1.4, r: -5.0, facing: 0.8, pose: 'stand' },
              { who: 'ludo', q: 0.6, r: -3.8, facing: 0.9, pose: 'stand' },
              { who: 'arriero', q: -0.6, r: -2.8, facing: 1.0, pose: 'stand' },
            ],
          },
        },
        {
          // A small grave at the foot of the tower, an uncut stone. The morning sun over the nave through the broken roof,
          // the lamps still lit, small against the sun. Below, the whole south face in the morning, smoke from the
          // terraces and no black corral.
          kind: 'scene',
          id: 'epilogue',
          lines: [
            ln('l1', 'ludo'),
            ln('l2', 'ludo'),
            ln('l3', 'piet', { flag: 'askedLamp' }),
            ln('l4', 'joran', { flag: 'askedJoran' }),
            ln('l5', 'joran', { flag: 'askedJoran' }),
            ln('l6', 'narrator'),
            ln('l7', 'piet', { flag: 'speechDawn' }),
            ln('l8', 'piet', { flag: 'speechSeen' }),
            ln('l9', 'joran', { flag: 'askedLetters' }),
            ln('l10', 'joran', { flag: 'askedLetters' }),
            ln('l11', 'piet', TRUTH),
            ln('l12', 'joran', TRUTH),
            ln('l13', 'piet', BELIEVE),
            ln('l14', 'ludo', BELIEVE),
            ln('l15', 'joran', BELIEVE),
            ln('l16', 'narrator'),
          ],
          cine: {
            video: 'senda-cleric-5-end',
            place: 'ermitaTomb',
            sky: 'dawn',
            cam: [
              { from: [2.4, 2.6, 1.4], at: [-0.2, -0.6, 0.5], ms: 10000 },
              // Over their shoulders and out: the whole south face in the morning.
              { from: [-0.6, -0.4, 2.2], at: [-6.0, 14.0, -6.0], ms: 12000 },
            ],
            figures: [
              { who: 'piet', q: -0.8, r: 0.4, facing: 5.2, pose: 'stand' },
              { who: 'joran', q: 0.6, r: 0.0, facing: 4.8, pose: 'stand' },
              { who: 'ludo', q: 1.4, r: 0.6, facing: 4.4, pose: 'stand' },
              { who: 'apprenticeA', q: -1.6, r: 1.2, facing: 5.4, pose: 'stand' },
              { who: 'apprenticeB', q: 0.2, r: 1.4, facing: 4.8, pose: 'stand' },
              { who: 'hero', q: 1.0, r: 1.4, facing: 4.4, pose: 'stand' },
            ],
          },
        },
      ],
    },
  ],
};
